import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';

export interface FaqItem {
  q: string;
  a: string;
}

interface SeoIntroFaqProps {
  intro: React.ReactNode;
  faqs: FaqItem[];
  /** Internal link cluster shown above the FAQ, e.g. cities or neighborhoods */
  internalLinks?: { label: string; href: string }[];
  internalLinksTitle?: string;
}

/**
 * Crawlable copy block + FAQPage JSON-LD designed to fix
 * "Crawled – currently not indexed" verdicts on listing index pages.
 * Keep it text-heavy — Google needs ~300+ words of unique content
 * for thin index pages to clear the quality bar.
 */
export function SeoIntroFaq({ intro, faqs, internalLinks, internalLinksTitle }: SeoIntroFaqProps) {
  const faqJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqs.map((f) => ({
      '@type': 'Question',
      name: f.q,
      acceptedAnswer: { '@type': 'Answer', text: f.a },
    })),
  };

  return (
    <section className="container py-10 border-t mt-6">
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(faqJsonLd)}</script>
      </Helmet>

      <div className="prose prose-sm md:prose max-w-3xl text-foreground/90">
        {intro}
      </div>

      {internalLinks && internalLinks.length > 0 && (
        <div className="mt-6">
          {internalLinksTitle && (
            <h2 className="text-base font-semibold mb-3">{internalLinksTitle}</h2>
          )}
          <ul className="flex flex-wrap gap-2 text-xs">
            {internalLinks.map((l) => (
              <li key={l.href}>
                <Link
                  to={l.href}
                  className="inline-block rounded-full border border-border bg-muted/40 px-3 py-1 hover:bg-primary/10 hover:text-primary transition-colors"
                >
                  {l.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="mt-8 max-w-3xl">
        <h2 className="text-lg md:text-xl font-bold mb-4">Frequently asked questions</h2>
        <dl className="space-y-4">
          {faqs.map((f) => (
            <div key={f.q}>
              <dt className="font-semibold text-sm md:text-base">{f.q}</dt>
              <dd className="mt-1 text-sm text-muted-foreground">{f.a}</dd>
            </div>
          ))}
        </dl>
      </div>
    </section>
  );
}
